import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import de from '@angular/common/locales/de';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
  CoreModule,
  CustomMatPaginatorIntl,
  IDENTITY_CONFIG,
  consoleLoggerProviders,
  loggingHttpInterceptorProviders,
} from '@core';
import { appInitializerProviders } from '@core/initializers';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { globalErrorHandlerProviders } from '@shared';
import { ToastrModule } from 'ngx-toastr';
// Angular CLI environment
import { MatPaginatorIntl } from '@angular/material/paginator';
import {
  API_BASE_URL,
  DokumentClient,
  DokumenteigenschaftClient,
  FilterClient,
  FiltergruppeClient,
  KfmaoCodeClient,
  StandortClient,
} from '@api/AffinisDIDApiClient';
import { MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '@env/environment';
import { AppComponent } from './app.component';
import { RoutesModule } from './routes/routes.module';
import { SharedModule } from './shared/shared.module';
import { ThemeModule } from './theme/theme.module';

// Import MSAL, MSAL browser libraries & Azure AD B2C configuration
import { msalConfig, protectedResources } from '../../auth-config';
import { AuthenticationModule } from './routes/authentication/authentication.module';

registerLocaleData(de);

// Required for AOT compilation
export function TranslateHttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslateHttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'de-DE',
    }),
    CoreModule.forRoot(),
    ThemeModule,
    SharedModule,
    RoutesModule,
    MsalModule.forRoot(
      new PublicClientApplication(msalConfig),
      {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: protectedResources.filterGruppe.scopes,
        },
      },
      {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
          [protectedResources.filterGruppe.endpoint, protectedResources.filterGruppe.scopes],
          [protectedResources.filter.endpoint, protectedResources.filter.scopes],
          [protectedResources.dokument.endpoint, protectedResources.dokument.scopes],
          [protectedResources.standort.endpoint, protectedResources.standort.scopes],
          [protectedResources.kfmaoCode.endpoint, protectedResources.kfmaoCode.scopes],
        ]),
      }
    ),
    FlexLayoutModule,
    AuthenticationModule
  ],
  providers: [
    loggingHttpInterceptorProviders,
    appInitializerProviders,
    consoleLoggerProviders,
    globalErrorHandlerProviders,
    {
      provide: IDENTITY_CONFIG,
      useValue: {
        initialAuthorizedUrl: '/dashboard',
        unauthorizedUrl: '/login',
      },
    },
    { provide: LOCALE_ID, useValue: 'de' },
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntl,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalGuard,
    FiltergruppeClient,
    FilterClient,
    DokumentClient,
    StandortClient,
    DokumenteigenschaftClient,
    KfmaoCodeClient,
    { provide: API_BASE_URL, useFactory: () => environment.baseUrl },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
